import { useState, useRef, useEffect } from "react";
import { Paperclip, Smile, Send, FileText, Lock } from "lucide-react";
import { groupMessagesByDay } from "@/lib/format";

const Bubble = ({ item }) => {
  const mine = item.from === "agency";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {item.attachment?.type === "image" && (
          <div
            className={[
              "mb-1.5 overflow-hidden",
              mine ? "rounded-2xl rounded-br-md" : "rounded-2xl rounded-bl-md",
            ].join(" ")}
          >
            <img src={item.attachment.url} alt="" className="max-h-64 w-auto object-cover" />
          </div>
        )}
        {item.attachment?.type === "pdf" && (
          <div
            className={[
              "mb-1.5 flex items-center gap-2.5 px-3.5 py-2.5 border border-[#EEF2F5]",
              mine ? "bg-white rounded-2xl rounded-br-md" : "bg-white rounded-2xl rounded-bl-md",
            ].join(" ")}
          >
            <div className="h-9 w-9 rounded-lg bg-[#FEE2E2] flex items-center justify-center text-[#991B1B]">
              <FileText size={16} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-[#111111] truncate">
                {item.attachment.name}
              </p>
              <p className="text-[10.5px] text-[#9CA3AF]">
                PDF · {item.attachment.size || ""}
              </p>
            </div>
          </div>
        )}
        {item.text && (
          <div
            className={[
              "px-3.5 py-2 text-[14px] leading-snug",
              mine
                ? "bg-[#111111] text-white rounded-[20px] rounded-br-md"
                : "bg-[#F1F5F9] text-[#111111] rounded-[20px] rounded-bl-md",
            ].join(" ")}
          >
            {item.text}
          </div>
        )}
        <span className={`mt-1 text-[10.5px] text-[#9CA3AF] px-1 ${mine ? "text-right" : ""}`}>
          {item.author} · {item.time}
        </span>
      </div>
    </div>
  );
};

const TabMessages = ({ messages = [], client = "", readOnly = false, onSend }) => {
  const groups = groupMessagesByDay(messages);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const initials = (client || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const send = async () => {
    if (!text.trim() || sending || readOnly) return;
    setSending(true);
    try {
      await onSend?.(text);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wyvio-card overflow-hidden" data-testid="tab-content-messages">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        <div className="flex flex-col h-[640px]">
          <div className="px-6 py-4 border-b border-[#EEF2F5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#111111] to-[#374151] text-white text-[12px] font-semibold flex items-center justify-center">
                {initials}
              </div>
              <div>
                <p className="text-[13.5px] font-semibold text-[#111111] leading-none">
                  {client || "Client"}
                </p>
                <p className="mt-1 text-[11px] text-[#22C55E]">
                  · Discussion privée
                </p>
              </div>
            </div>
            <span className="text-[10.5px] font-medium text-[#22C55E]">
              Chiffré · privé
            </span>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-[#FAFBFC]">
            {groups.length === 0 && (
              <p className="text-center text-[12.5px] text-[#9CA3AF] py-10">
                Aucun message pour le moment.
              </p>
            )}
            {groups.map((group) => (
              <div key={group.id}>
                <div className="flex items-center justify-center mb-3">
                  <span className="text-[10.5px] font-medium text-[#9CA3AF] bg-white px-2.5 py-0.5 rounded-full border border-[#EEF2F5]">
                    {group.day}
                  </span>
                </div>
                <div className="space-y-2.5">
                  {group.items.map((it) => (
                    <Bubble key={it.id} item={it} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-[#EEF2F5] bg-white">
            {readOnly ? (
              <div className="h-11 rounded-full border border-[#EEF2F5] bg-[#F7F8FA] px-4 flex items-center justify-center gap-2 text-[12.5px] font-medium text-[#6B7280]">
                <Lock size={12} strokeWidth={2} />
                Messagerie fermée — dossier terminé
              </div>
            ) : (
              <div className="flex items-center gap-2 h-11 rounded-full border border-[#EEF2F5] bg-[#F7F8FA] px-3">
                <button
                  data-testid="message-attach"
                  className="h-8 w-8 rounded-full hover:bg-white flex items-center justify-center text-[#6B7280]"
                  aria-label="Pièce jointe"
                >
                  <Paperclip size={15} strokeWidth={2} />
                </button>
                <input
                  data-testid="message-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={`Écrivez un message à ${client?.split(" ")[0] || "votre client"}…`}
                  className="flex-1 bg-transparent outline-none text-[13.5px] placeholder:text-[#9CA3AF] text-[#111111]"
                />
                <button
                  data-testid="message-emoji"
                  className="h-8 w-8 rounded-full hover:bg-white flex items-center justify-center text-[#6B7280]"
                  aria-label="Emoji"
                >
                  <Smile size={15} strokeWidth={2} />
                </button>
                <button
                  data-testid="message-send"
                  onClick={send}
                  disabled={sending || !text.trim()}
                  className="h-8 w-8 rounded-full bg-[#111111] text-white flex items-center justify-center hover:bg-black transition-colors disabled:opacity-40"
                  aria-label="Envoyer"
                >
                  <Send size={13} strokeWidth={2.25} />
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="hidden lg:flex flex-col border-l border-[#EEF2F5] bg-white">
          <div className="p-5 border-b border-[#EEF2F5]">
            <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
              Conversation
            </p>
            <p className="mt-2 text-[13px] text-[#111111] leading-relaxed">
              Cette discussion est privée entre l'agence et le client. Le client ne voit rien d'autre.
            </p>
          </div>

          <div className="p-5 mt-auto">
            <button
              data-testid="messages-mark-read"
              className="w-full h-9 rounded-full bg-[#F7F8FA] hover:bg-[#EEF2F5] text-[12px] font-medium text-[#111111] transition-colors"
            >
              Marquer comme lu
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TabMessages;
